import {
  groupToolsByBehavior,
  type ProviderTool,
  type ProviderTools,
  type ToolBehavior,
  type ToolBehaviorGroup,
  type ToolPermission,
} from '@littlebird/integrations';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import { ToolAskFirstSwitch } from './tool-permission-control';

export interface ToolsTabProps {
  tools: readonly ProviderTool[];
  discovery: ProviderTools['discovery'] | undefined;
  isInitialLoading: boolean;
  loadErrorMessage: string | null;
  isRefetchError: boolean;
  retry(): void;
  onSetToolPermission(toolSlug: string, permission: ToolPermission): void;
  onSetBehaviorPermission(
    behavior: ToolBehavior,
    permission: ToolPermission
  ): void;
  failureMessage: string | null;
}

const GROUP_CONTROL_DISABLED_HINT =
  "Reconnect this integration to change the whole group at once — its tools can't be loaded right now.";

function ToolsHeader() {
  return (
    <YStack className="gap-0.5">
      <p className="text-sm font-medium text-foreground">
        What Littlebird may do here
      </p>
      <p className="text-xs text-foreground-muted">
        Littlebird runs these tools on its own. Turn on Ask first for anything
        you want to approve every time.
      </p>
    </YStack>
  );
}

/** The full description on hover, but only when the row actually trimmed it —
 * whether the text overflows can't be known until it renders, so it's
 * measured on mouse-enter and the tooltip stays disabled for text that fits. */
function TruncatedDescription({ text }: { text: string }) {
  const [truncated, setTruncated] = useState(false);
  return (
    <Tooltip
      label={text}
      disabled={!truncated}
      side="top"
      contentClassName="max-w-md"
    >
      <p
        className="truncate text-xs text-foreground-muted"
        onMouseEnter={(event) =>
          setTruncated(
            event.currentTarget.scrollWidth > event.currentTarget.clientWidth
          )
        }
      >
        {text}
      </p>
    </Tooltip>
  );
}

function ToolRow({
  tool,
  onSetPermission,
}: {
  tool: ProviderTool;
  onSetPermission(permission: ToolPermission): void;
}) {
  return (
    <XStack className="items-center justify-between gap-7 px-4 py-2.5">
      <YStack className="min-w-0 gap-0.5">
        <p className="truncate text-sm text-foreground">{tool.display_name}</p>
        {tool.display_description && (
          <TruncatedDescription text={tool.display_description} />
        )}
      </YStack>
      <ToolAskFirstSwitch
        name={tool.display_name}
        permission={tool.permission}
        onChange={onSetPermission}
      />
    </XStack>
  );
}

/** Group-level permission dropdown: two choices, Ask first and Auto run,
 * with a check on the current one. A split group surfaces a non-selectable
 * "Mixed" entry at the top of the list (and on the trigger) instead of
 * pretending either choice applies. `deny` counts as "ask first", matching
 * the per-row display rule. Disabled only when discovery has no working
 * account to answer for — a group write would 409 with nothing to cover. */
function GroupPermissionMenu({
  groupLabel,
  tools,
  disabled,
  onSelect,
}: {
  groupLabel: string;
  tools: readonly ProviderTool[];
  disabled: boolean;
  onSelect(permission: ToolPermission): void;
}) {
  const total = tools.length;
  const askFirstCount = tools.filter(
    (tool) => tool.permission !== 'allow'
  ).length;
  const selection =
    total > 0 && askFirstCount === total
      ? 'ask'
      : askFirstCount === 0
        ? 'allow'
        : 'mixed';
  const triggerLabel =
    selection === 'ask'
      ? 'Ask first'
      : selection === 'allow'
        ? 'Auto run'
        : 'Mixed';

  const trigger = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`Set ${groupLabel} group permission`}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border-strong bg-surface px-2.5 py-1 text-xs text-foreground transition-colors',
            disabled
              ? 'pointer-events-none cursor-not-allowed opacity-50'
              : 'hover:bg-surface-hover'
          )}
        >
          {triggerLabel}
          <Icons.chevronDown className="size-3.5 text-foreground-muted" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" fit>
        {selection === 'mixed' && (
          <DropdownMenu.Item disabled className="min-w-32">
            <Icons.check className="size-3.5" />
            <span className="flex-1">Mixed</span>
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item
          onSelect={() => onSelect('ask')}
          className="min-w-32"
        >
          <Icons.check
            className={cn('size-3.5', selection !== 'ask' && 'invisible')}
          />
          <span className="flex-1">Ask first</span>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => onSelect('allow')}
          className="min-w-32"
        >
          <Icons.check
            className={cn('size-3.5', selection !== 'allow' && 'invisible')}
          />
          <span className="flex-1">Auto run</span>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );

  if (!disabled) return trigger;

  // The trigger button is natively `disabled`, so it never receives pointer
  // events for Tooltip's hover detection — `pointer-events-none` above lets
  // hover pass through to this focusable span instead, which carries the
  // Tooltip trigger.
  return (
    <Tooltip label={GROUP_CONTROL_DISABLED_HINT}>
      <span tabIndex={0} className="inline-flex shrink-0 cursor-not-allowed">
        {trigger}
      </span>
    </Tooltip>
  );
}

function BehaviorGroupSection({
  group,
  groupControlDisabled,
  onSetToolPermission,
  onSetBehaviorPermission,
}: {
  group: ToolBehaviorGroup;
  groupControlDisabled: boolean;
  onSetToolPermission(toolSlug: string, permission: ToolPermission): void;
  onSetBehaviorPermission(
    behavior: ToolBehavior,
    permission: ToolPermission
  ): void;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <YStack className="gap-1.5">
      <XStack className="items-center gap-2 px-0.5">
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={
            expanded ? `Collapse ${group.label}` : `Expand ${group.label}`
          }
          onClick={() => setExpanded((current) => !current)}
          className="flex items-center"
        >
          <Icons.chevronDown
            className={cn(
              'size-4 text-foreground-muted transition-transform',
              !expanded && '-rotate-90'
            )}
          />
        </button>
        {/* Sibling, not a button descendant — a heading isn't valid button content. */}
        <h3 className="text-sm font-medium text-foreground">{group.label}</h3>
        <span className="rounded-full bg-secondary px-1.5 font-mono text-xs text-foreground-muted">
          {group.tools.length}
        </span>
        <div className="flex-1" />
        <GroupPermissionMenu
          groupLabel={group.label}
          tools={group.tools}
          disabled={groupControlDisabled}
          onSelect={(permission) =>
            onSetBehaviorPermission(group.behavior, permission)
          }
        />
      </XStack>
      {expanded && (
        <YStack className="divide-y divide-border/70 overflow-hidden rounded-lg border border-border bg-surface dark:divide-border-strong dark:border-border-strong">
          {group.tools.map((tool) => (
            <ToolRow
              key={tool.tool_slug}
              tool={tool}
              onSetPermission={(permission) =>
                onSetToolPermission(tool.tool_slug, permission)
              }
            />
          ))}
        </YStack>
      )}
    </YStack>
  );
}

export function ToolsTab({
  tools,
  discovery,
  isInitialLoading,
  loadErrorMessage,
  isRefetchError,
  retry,
  onSetToolPermission,
  onSetBehaviorPermission,
  failureMessage,
}: ToolsTabProps) {
  if (isInitialLoading) {
    return (
      <YStack center sizeFull className="gap-2 py-16">
        <Icons.loader className="size-4 animate-spin text-foreground-muted" />
        <p className="text-xs text-foreground-muted">Loading tools...</p>
      </YStack>
    );
  }

  if (loadErrorMessage) {
    return (
      <YStack center sizeFull className="gap-3 py-16 text-center">
        <p className="text-xs text-foreground-muted">{loadErrorMessage}</p>
        <Button size="sm" variant="secondary" onClick={retry}>
          Retry
        </Button>
      </YStack>
    );
  }

  // 'unavailable' means discovery couldn't get a fresh read from any
  // account, not that the provider genuinely has no tools — per-account
  // detail lives on the Accounts tab, so this tab only distinguishes
  // "couldn't load" from a real empty list.
  if (discovery?.status === 'unavailable' && tools.length === 0) {
    return (
      <YStack center sizeFull className="gap-3 py-16 text-center">
        <p className="text-xs text-foreground-muted">
          Couldn&apos;t load tools for this provider.
        </p>
        <Button size="sm" variant="secondary" onClick={retry}>
          Retry
        </Button>
      </YStack>
    );
  }

  const groups = groupToolsByBehavior(tools);
  const groupControlDisabled = discovery?.status === 'unavailable';

  return (
    <YStack className="gap-4 p-6">
      <ToolsHeader />
      {isRefetchError && (
        <XStack className="items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-3">
          <p className="text-xs text-foreground-muted">
            Couldn&apos;t refresh tools. Showing the last known list.
          </p>
          <Button size="sm" variant="secondary" onClick={retry}>
            Retry
          </Button>
        </XStack>
      )}
      {failureMessage && (
        <p className="text-xs text-destructive">{failureMessage}</p>
      )}
      {groups.length === 0 ? (
        <p className="text-xs text-foreground-muted">No tools available</p>
      ) : (
        groups.map((group) => (
          <BehaviorGroupSection
            key={group.behavior}
            group={group}
            groupControlDisabled={groupControlDisabled}
            onSetToolPermission={onSetToolPermission}
            onSetBehaviorPermission={onSetBehaviorPermission}
          />
        ))
      )}
    </YStack>
  );
}
